import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { MessageSquare, Plus, Send, HardHat, Archive, BellOff, Search as SearchIcon } from "lucide-react";

const MessagesPage = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [conversationDetail, setConversationDetail] = useState(null);
  const [users, setUsers] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [query, setQuery] = useState("");
  const [isComposeOpen, setIsComposeOpen] = useState(false);
  const [newConversation, setNewConversation] = useState({ title: "", description: "", participant_user_ids: [], participant_shared_worker_identity_ids: [], initial_message: "" });
  const [newMessage, setNewMessage] = useState("");

  const fetchInbox = async (search = "") => {
    try {
      const [conversationsRes, usersRes, contractorsRes] = await Promise.all([
        axios.get(`${API}/messages/conversations`, { params: search ? { query: search } : {} }),
        axios.get(`${API}/users`),
        axios.get(`${API}/messages/users/contractor-identities`),
      ]);
      const items = conversationsRes.data || [];
      setConversations(items);
      setUsers((usersRes.data || []).filter((item) => item.id !== user?.id));
      setContractors(contractorsRes.data || []);
      if (!selectedConversation && items.length > 0) {
        setSelectedConversation(items[0].id);
      }
    } catch {
      toast.error("Failed to load inbox");
    }
  };

  const openConversation = async (conversationId) => {
    try {
      const response = await axios.get(`${API}/messages/conversations/${conversationId}`);
      setSelectedConversation(conversationId);
      setConversationDetail(response.data);
      await axios.post(`${API}/messages/conversations/${conversationId}/read`, {});
      setConversations((current) => current.map((item) => item.id === conversationId ? { ...item, unread_count: 0 } : item));
    } catch {
      toast.error("Failed to open conversation");
    }
  };

  useEffect(() => {
    fetchInbox();
    axios.post(`${API}/messages/presence`).catch(() => {});
    const interval = setInterval(() => {
      axios.post(`${API}/messages/presence`).catch(() => {});
      if (selectedConversation) {
        openConversation(selectedConversation);
      }
    }, 15000);
    return () => clearInterval(interval);
  }, [selectedConversation]);

  useEffect(() => {
    const conversationId = searchParams.get("conversation");
    if (conversationId) {
      openConversation(conversationId);
    }
  }, [searchParams]);

  const selectedParticipantNames = useMemo(() => {
    const internal = users
      .filter((item) => newConversation.participant_user_ids.includes(item.id))
      .map((item) => item.full_name || item.email);
    const contractorNames = contractors
      .filter((item) => newConversation.participant_shared_worker_identity_ids.includes(item.shared_worker_identity_id))
      .map((item) => `${item.full_name || item.email}${item.company_name ? ` • ${item.company_name}` : ""}`);
    return [...internal, ...contractorNames];
  }, [users, contractors, newConversation]);

  const handleCreateConversation = async (e) => {
    e.preventDefault();
    if (newConversation.participant_user_ids.length === 0 && newConversation.participant_shared_worker_identity_ids.length === 0) {
      toast.error("Pick at least one participant");
      return;
    }
    try {
      const response = await axios.post(`${API}/messages/conversations`, newConversation);
      setNewConversation({ title: "", description: "", participant_user_ids: [], participant_shared_worker_identity_ids: [], initial_message: "" });
      setIsComposeOpen(false);
      toast.success("Conversation created");
      await fetchInbox(query);
      await openConversation(response.data.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create conversation");
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!selectedConversation || !newMessage.trim()) return;
    try {
      await axios.post(`${API}/messages/conversations/${selectedConversation}/messages`, { body: newMessage.trim() });
      setNewMessage("");
      await openConversation(selectedConversation);
      await fetchInbox(query);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send message");
    }
  };

  const updateConversationSettings = async (settings) => {
    if (!selectedConversation) return;
    try {
      await axios.put(`${API}/messages/conversations/${selectedConversation}/settings`, settings);
      toast.success(settings.archive ? "Conversation archived" : settings.mute ? "Conversation muted" : "Conversation updated");
      setSelectedConversation(null);
      setConversationDetail(null);
      await fetchInbox(query);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update conversation");
    }
  };

  const toggleInternalParticipant = (userId) => {
    setNewConversation((current) => ({
      ...current,
      participant_user_ids: current.participant_user_ids.includes(userId)
        ? current.participant_user_ids.filter((id) => id !== userId)
        : [...current.participant_user_ids, userId],
    }));
  };

  const toggleContractorParticipant = (identityId) => {
    setNewConversation((current) => ({
      ...current,
      participant_shared_worker_identity_ids: current.participant_shared_worker_identity_ids.includes(identityId)
        ? current.participant_shared_worker_identity_ids.filter((id) => id !== identityId)
        : [...current.participant_shared_worker_identity_ids, identityId],
    }));
  };

  const runSearch = async (e) => {
    e?.preventDefault?.();
    await fetchInbox(query);
  };

  const activeConversationTitle = conversationDetail?.title || "Select a conversation";
  const activeConversationSubtitle = conversationDetail
    ? conversationDetail.participants.map((p) => p.full_name).filter(Boolean).join(", ")
    : "Choose a thread from the inbox to read and reply.";

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Messages</h1>
          <p className="text-muted-foreground mt-1">A cleaner inbox for team and contractor conversations.</p>
        </div>
        <Button onClick={() => setIsComposeOpen(true)} className="bg-primary hover:bg-primary/90 md:w-auto w-full">
          <Plus className="h-4 w-4 mr-2" />
          New Conversation
        </Button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[380px_minmax(0,1fr)] gap-6">
        <Card className="min-h-[720px]">
          <CardHeader className="space-y-4">
            <div>
              <CardTitle className="flex items-center gap-2"><MessageSquare className="h-4 w-4" /> Inbox</CardTitle>
              <CardDescription>Your conversations, all in one place.</CardDescription>
            </div>
            <form onSubmit={runSearch} className="flex gap-2">
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search inbox..."
                className="bg-muted/50"
              />
              <Button type="submit" variant="outline" size="icon">
                <SearchIcon className="h-4 w-4" />
              </Button>
            </form>
          </CardHeader>
          <CardContent className="space-y-3">
            {conversations.length > 0 ? conversations.map((conversation) => (
              <button
                key={conversation.id}
                onClick={() => openConversation(conversation.id)}
                className={`w-full rounded-xl border p-3 text-left transition ${selectedConversation === conversation.id ? "border-primary bg-primary/10" : "border-border/60 hover:bg-muted/30"}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{conversation.title || conversation.participants.map((p) => p.full_name).filter(Boolean).join(", ")}</div>
                    <div className="text-sm text-muted-foreground mt-1 line-clamp-2">{conversation.latest_message?.body || "No messages yet"}</div>
                  </div>
                  {conversation.unread_count > 0 && <Badge>{conversation.unread_count}</Badge>}
                </div>
              </button>
            )) : <p className="text-sm text-muted-foreground">No conversations yet.</p>}
          </CardContent>
        </Card>

        <Card className="min-h-[720px]">
          <CardHeader>
            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
              <div>
                <CardTitle>{activeConversationTitle}</CardTitle>
                <CardDescription>{activeConversationSubtitle}</CardDescription>
                {conversationDetail?.participants?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {conversationDetail.participants.map((p, idx) => (
                      <Badge key={`${p.full_name}-${idx}`} variant={p.is_online ? "default" : "secondary"}>
                        {p.full_name || p.email || p.participant_type}{p.is_online ? " • online" : ""}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
              {selectedConversation && (
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => updateConversationSettings({ mute: true })}>
                    <BellOff className="h-4 w-4 mr-1" />Mute
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => updateConversationSettings({ archive: true })}>
                    <Archive className="h-4 w-4 mr-1" />Archive
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="min-h-[560px] rounded-xl border border-border/60 bg-muted/20 p-4 space-y-3">
              {conversationDetail?.messages?.length > 0 ? conversationDetail.messages.map((message) => (
                <div
                  key={message.id}
                  className={`max-w-[80%] rounded-xl p-3 ${message.sender_user_id === user?.id ? "ml-auto bg-primary text-primary-foreground" : "bg-card border border-border/60"}`}
                >
                  <div className="text-xs opacity-80 mb-1">{message.sender_name || message.sender_type}</div>
                  <div className="text-sm whitespace-pre-wrap">{message.body}</div>
                  <div className="text-[11px] opacity-80 mt-2">{message.read_count > 0 ? `Read by ${message.read_count}` : "Sent"}</div>
                </div>
              )) : <div className="text-sm text-muted-foreground">No messages in this conversation yet.</div>}
            </div>
            <form onSubmit={handleSendMessage} className="flex gap-3">
              <Input
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Write a message..."
                className="bg-muted/50"
                disabled={!selectedConversation}
              />
              <Button type="submit" disabled={!selectedConversation || !newMessage.trim()} className="bg-primary hover:bg-primary/90">
                <Send className="h-4 w-4" />
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      <Dialog open={isComposeOpen} onOpenChange={setIsComposeOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>New Conversation</DialogTitle>
            <DialogDescription>Start a thread with your team or linked subcontractor participants.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateConversation} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Title</Label>
                <Input value={newConversation.title} onChange={(e) => setNewConversation({ ...newConversation, title: e.target.value })} placeholder="Optional thread title" className="bg-muted/50" />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Input value={newConversation.description} onChange={(e) => setNewConversation({ ...newConversation, description: e.target.value })} placeholder="Optional context" className="bg-muted/50" />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Selected Participants</Label>
              <div className="flex flex-wrap gap-2 min-h-8">
                {selectedParticipantNames.length > 0 ? selectedParticipantNames.map((name) => <Badge key={name} variant="secondary">{name}</Badge>) : <span className="text-sm text-muted-foreground">No participants selected</span>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Internal Team</Label>
                <div className="max-h-52 overflow-y-auto rounded-lg border border-border/60 p-3 space-y-2">
                  {users.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => toggleInternalParticipant(item.id)}
                      className={`w-full rounded-md border p-2 text-left text-sm ${newConversation.participant_user_ids.includes(item.id) ? "border-primary bg-primary/10" : "border-border/50"}`}
                    >
                      <div className="font-medium">{item.full_name || item.email}</div>
                      <div className="text-muted-foreground">{item.email}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Contractor Participants</Label>
                <div className="max-h-52 overflow-y-auto rounded-lg border border-border/60 p-3 space-y-2">
                  {contractors.length > 0 ? contractors.map((item) => (
                    <button
                      key={item.shared_worker_identity_id}
                      type="button"
                      onClick={() => toggleContractorParticipant(item.shared_worker_identity_id)}
                      className={`w-full rounded-md border p-2 text-left text-sm ${newConversation.participant_shared_worker_identity_ids.includes(item.shared_worker_identity_id) ? "border-primary bg-primary/10" : "border-border/50"}`}
                    >
                      <div className="font-medium flex items-center gap-2"><HardHat className="h-4 w-4" />{item.full_name || item.email}</div>
                      <div className="text-muted-foreground">{item.email}{item.company_name ? ` • ${item.company_name}` : ""}</div>
                    </button>
                  )) : <div className="text-sm text-muted-foreground">No linked contractor identities available yet.</div>}
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Initial Message</Label>
              <Textarea value={newConversation.initial_message} onChange={(e) => setNewConversation({ ...newConversation, initial_message: e.target.value })} placeholder="Say hello..." className="bg-muted/50 min-h-28" />
            </div>

            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setIsComposeOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-primary hover:bg-primary/90">Create Conversation</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MessagesPage;
